import { useState, type CSSProperties } from 'react';
import { apiPatch } from '../../api/client';
import { pastelFromBackground } from './leadPastels';

export const LEAD_REASON_OPTIONS = [
  { id: 'unable_to_reach', label: 'No se pudo contactar' },
  { id: 'inaccurate_data', label: 'Datos inválidos / spam' },
  { id: 'no_need', label: 'Sin interés ni necesidad' },
  { id: 'no_budget', label: 'Sin presupuesto' },
  { id: 'no_fit', label: 'No es el perfil' },
  { id: 'not_ready', label: 'No está listo ahora' },
  { id: 'no_authority', label: 'No decide la compra' },
  { id: 'other', label: 'Otro' },
] as const;

export const LEAD_FIT_OPTIONS = [
  { id: 'consumer', label: 'Consumidor' },
  { id: 'no_business', label: 'Sin negocio' },
  { id: 'wrong_geo', label: 'Otro país / plaza' },
  { id: 'too_small', label: 'Demasiado chica' },
] as const;

export type LeadFields = {
  leadQuality: string | null;
  leadReason: string | null;
  leadFitDetails?: string[];
  leadNote: string | null;
  leadRatedSource: string | null;
};

type LeadState = { id: string; label: string; color?: string };

type Props = {
  conversationId: string;
  token: string | null;
  canWrite: boolean;
  value: LeadFields;
  states?: LeadState[] | null;
  compact?: boolean;
  onSaved: (next: LeadFields) => void;
};

export function leadQualityChipLabel(quality: string | null | undefined, states?: LeadState[] | null): string | null {
  if (!quality) return null;
  const custom = states?.find((s) => s.id === quality)?.label;
  if (custom) return custom;
  if (quality === 'qualified') return 'Calificado';
  if (quality === 'unqualified') return 'No calificado';
  return quality;
}

export function LeadQualityEditor({ conversationId, token, canWrite, value, states, compact, onSaved }: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [draftQuality, setDraftQuality] = useState<string | null | undefined>(undefined);
  const quality = draftQuality !== undefined ? draftQuality : value.leadQuality;

  const save = async (patch: Partial<LeadFields> & { leadQuality: string | null }) => {
    if (!canWrite || !token) return;
    setBusy(true);
    setErr(null);
    try {
      const body = {
        leadQuality: patch.leadQuality,
        leadReason: patch.leadQuality === 'unqualified' ? (patch.leadReason ?? value.leadReason) : null,
        leadFitDetails:
          (patch.leadReason ?? value.leadReason) === 'no_fit' && patch.leadQuality === 'unqualified'
            ? (patch.leadFitDetails ?? value.leadFitDetails ?? [])
            : [],
        leadNote: patch.leadNote !== undefined ? patch.leadNote : value.leadNote,
      };
      const data = await apiPatch<LeadFields>(`/api/console/conversations/${conversationId}`, body, token);
      onSaved({
        leadQuality: data.leadQuality ?? null,
        leadReason: data.leadReason ?? null,
        leadFitDetails: data.leadFitDetails ?? [],
        leadNote: data.leadNote ?? null,
        leadRatedSource: data.leadRatedSource ?? 'human',
      });
      setDraftQuality(undefined);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const btn = (active: boolean): CSSProperties => ({
    padding: compact ? '5px 10px' : '6px 12px',
    borderRadius: 6,
    border: active ? '1px solid #7c3aed' : '1px solid #e2e8f0',
    background: active ? '#f5f3ff' : '#fff',
    color: active ? '#5b21b6' : '#334155',
    fontSize: 12,
    fontWeight: 600,
    cursor: canWrite && !busy ? 'pointer' : 'default',
    fontFamily: 'inherit',
    opacity: busy ? 0.6 : 1,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>Lead</span>
        {value.leadRatedSource === 'llm' ? (
          <span style={{ fontSize: 11, color: '#64748b' }}>sugerido por el agente</span>
        ) : null}
      </div>
      {states && states.length > 0 ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {states.map((state, index) => {
            const pastel = pastelFromBackground(state.color, index);
            const active = quality === state.id;
            return (
            <button
              key={state.id}
              type="button"
              style={{
                ...btn(active),
                border: active ? `1px solid ${pastel.color}` : '1px solid transparent',
                background: pastel.background,
                color: pastel.color,
              }}
              disabled={!canWrite || busy}
              onClick={() => void save({ leadQuality: quality === state.id ? null : state.id })}
            >
              {state.label}
            </button>
            );
          })}
        </div>
      ) : (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button
          type="button"
          style={btn(quality === 'qualified')}
          disabled={!canWrite || busy}
          onClick={() => {
            if (quality === 'qualified') {
              void save({ leadQuality: null });
              return;
            }
            void save({ leadQuality: 'qualified', leadReason: null, leadFitDetails: [] });
          }}
        >
          Calificado
        </button>
        <button
          type="button"
          style={btn(quality === 'unqualified')}
          disabled={!canWrite || busy}
          onClick={() => {
            if (quality === 'unqualified') {
              void save({ leadQuality: null });
              return;
            }
            setDraftQuality('unqualified');
          }}
        >
          No calificado
        </button>
      </div>
      )}
      {!states?.length && quality === 'unqualified' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {LEAD_REASON_OPTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              style={btn(value.leadReason === r.id)}
              disabled={!canWrite || busy}
              onClick={() => void save({ leadQuality: 'unqualified', leadReason: r.id })}
            >
              {r.label}
            </button>
          ))}
        </div>
      ) : null}
      {quality === 'unqualified' && value.leadReason === 'no_fit' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {LEAD_FIT_OPTIONS.map((f) => {
            const on = (value.leadFitDetails ?? []).includes(f.id);
            return (
              <button
                key={f.id}
                type="button"
                style={btn(on)}
                disabled={!canWrite || busy}
                onClick={() => {
                  const cur = value.leadFitDetails ?? [];
                  const next = on ? cur.filter((x) => x !== f.id) : [...cur, f.id];
                  void save({ leadQuality: 'unqualified', leadReason: 'no_fit', leadFitDetails: next });
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      ) : null}
      {canWrite && (quality === 'unqualified' || quality === 'qualified') ? (
        <input
          type="text"
          defaultValue={value.leadNote ?? ''}
          placeholder="Nota (opcional)"
          maxLength={500}
          disabled={busy}
          onBlur={(e) => {
            const next = e.target.value.trim() || null;
            if (next === (value.leadNote ?? null)) return;
            void save({ leadQuality: quality, leadNote: next });
          }}
          style={{
            fontSize: 12,
            padding: '6px 8px',
            borderRadius: 6,
            border: '1px solid #e2e8f0',
            fontFamily: 'inherit',
          }}
        />
      ) : value.leadNote ? (
        <div style={{ fontSize: 12, color: '#475569' }}>{value.leadNote}</div>
      ) : null}
      {err ? <div style={{ fontSize: 12, color: '#b91c1c' }}>{err}</div> : null}
    </div>
  );
}
